import { useEffect, useState } from 'react';

import { ApiResponseObject } from '@api/v4/ApiResponseTypes';

import { Asset } from '@api/v4/assets/assetTypes';
import { Attachment } from '@api/v4/attachments/attachmentTypes';
import getResourceAssets from '@api/v4/resources/assets';

import { AssetViewModel } from './model';

export interface AssetsResponse {
  assets: ApiResponseObject<Asset>[];
  included: ApiResponseObject<Attachment>[];
}

function mapAssetsResponseToAssetViewModels(
  assetsResponse: AssetsResponse
): AssetViewModel[] {
  return assetsResponse.assets.map((asset) => {
    const attachments = asset.relationships.attachments.data.map(
      (attachment) => {
        return assetsResponse.included.find(
          (included) => included.id === attachment.id
        );
      }
    );

    return {
      ...asset,
      attachments: attachments || [],
    };
  });
}

async function fetchSectionAssets(
  sectionKey: string
): Promise<AssetViewModel[]> {
  const params = {
    ...(BFG.manifestDigest && { digest: BFG.manifestDigest }),
    include: 'attachments',
    fields: ['asset_data', 'is_downloadable', 'large_thumbnail_url', 'view_only', 'view_thumbnail_retina'],
  };

  const fetchOptions = {
    isCollection: BFG.resource.type === 'collection',
    resourceType: BFG.resource.type,
    resourceKey: BFG.resource.key,
    sectionKey,
  };

  const assetsResponse = (await getResourceAssets(
    params,
    fetchOptions
  )) as AssetsResponse;
  return mapAssetsResponseToAssetViewModels(assetsResponse);
}

async function fetchAssets(sectionKeys: string[]): Promise<AssetViewModel[]> {
  const assets = await Promise.all(sectionKeys.map(fetchSectionAssets));
  return assets.flat();
}

export interface UseFetchAssetsProps {
  sectionKeys: string[];
}

export interface UseFetchAssetsReturn {
  assets: AssetViewModel[];
  error: boolean;
  loading: boolean;
}

export const useFetchAssets: (
  props: UseFetchAssetsProps
) => UseFetchAssetsReturn = ({ sectionKeys }) => {
  const [assets, setAssets] = useState<AssetViewModel[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setError(false);
    fetchAssets(sectionKeys)
      .then((fetchedAssets) => {
        setAssets(fetchedAssets);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [sectionKeys]);

  return { assets, error, loading };
};
